/**
 * Pure helpers for the House Rules editor (PRD FR-2, FR-8.5). Nothing here
 * touches the network or the DOM, so every judgement the editor makes — what
 * a diff shows, what a version is called — is testable on its own.
 */
import type { DiffLine } from "./api";

/** FR-2.4 size cap. The server enforces it; the editor stops short of it. */
export const RULES_MAX_CHARS = 8000;

/** The counter is noise until it is nearly information. */
export const RULES_COUNTER_FROM = 7000;

// --- version history --------------------------------------------------------

/**
 * Server authors are `recipient` | `nob_proposal` (FR-8.5) plus the two the
 * onboarding paths write. The history says who, in the words a person uses.
 */
export function humaniseAuthor(author: string): string {
  const a = author.trim().toLowerCase();
  if (a.includes("rollback") || a.includes("restore")) return "rollback";
  if (a.startsWith("onboarding")) return "onboarding";
  if (
    a === "" ||
    a.includes("recipient") ||
    a.includes("owner") ||
    a.includes("user") ||
    a.includes("manual")
  ) {
    return "you";
  }
  return "Nob";
}

// --- sections (telemetry: sections_changed, Appendix B) ---------------------

/**
 * The document as its sections, in the order it writes them: keyed by the
 * heading folded to lower case, carrying the heading exactly as written. The
 * key under `""` is whatever stands above the first heading, which is the title
 * and not a section.
 */
function sectionsOf(markdown: string): Map<string, { heading: string; body: string }> {
  const map = new Map<string, { heading: string; body: string }>();
  let heading = "";
  let buffer: string[] = [];
  const close = () => {
    map.set(heading.toLowerCase(), { heading, body: buffer.join("\n").trim() });
  };
  for (const raw of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const found = /^(#{1,3})\s+(.*)$/.exec(raw.trimEnd());
    if (found?.[2] !== undefined) {
      close();
      heading = found[2].trim();
      buffer = [];
      continue;
    }
    buffer.push(raw);
  }
  close();
  return map;
}

export function sectionsChanged(before: string, after: string): number {
  const a = sectionsOf(before);
  const b = sectionsOf(after);
  let changed = 0;
  for (const key of new Set([...a.keys(), ...b.keys()])) {
    if ((a.get(key)?.body ?? "") !== (b.get(key)?.body ?? "")) changed += 1;
  }
  return changed;
}

/**
 * WHICH SECTIONS A CHANGE TOUCHES, BY NAME (ruling R-0915-1 (3)) — the console's
 * copy of what the api computes for a card it streams (`rules/parse.ts`), for
 * the cards it does not: `GET /rules/proposals` carries the proposed document
 * and no section list.
 *
 * Read off the TWO DOCUMENTS and never off the diff. A diff is lines with a
 * couple of lines of context, and the nearest heading above a changed line is
 * very often not in the window — so a diff reader either names the wrong
 * section or invents one, which is exactly what a chip must never do. The
 * headings come back as the resulting document writes them, in its order, with
 * a section that was removed named as the document that still has it wrote it.
 */
export function sectionsTouched(before: string, after: string): string[] {
  const from = sectionsOf(before);
  const to = sectionsOf(after);
  const touched: string[] = [];
  for (const [key, section] of to) {
    if (key === "") continue; // the title is not a section
    if ((from.get(key)?.body ?? null) !== section.body) touched.push(section.heading);
  }
  for (const [key, section] of from) {
    if (key === "" || to.has(key)) continue;
    touched.push(section.heading);
  }
  return touched;
}

// --- diff shaping -----------------------------------------------------------

export type DiffSegment =
  { kind: "line"; index: number; line: DiffLine } | { kind: "gap"; count: number };

export function hasChanges(lines: DiffLine[]): boolean {
  return lines.some((l) => l.type !== "same");
}

/**
 * A diff is read for what changed, so unchanged runs collapse to a count with
 * a couple of lines of context either side. Short runs are left alone — a gap
 * marker over two lines is more interruption than the lines themselves.
 */
export function groupDiff(lines: DiffLine[], context = 2, minGap = 3): DiffSegment[] {
  const all = (): DiffSegment[] => lines.map((line, index) => ({ kind: "line", index, line }));
  if (!hasChanges(lines)) return all();

  const keep = new Set<number>();
  lines.forEach((line, i) => {
    if (line.type === "same") return;
    for (let j = i - context; j <= i + context; j += 1) {
      if (j >= 0 && j < lines.length) keep.add(j);
    }
  });

  // Widen over gaps too short to be worth collapsing.
  let run = 0;
  for (let i = 0; i <= lines.length; i += 1) {
    if (i < lines.length && !keep.has(i)) {
      run += 1;
      continue;
    }
    if (run > 0 && run < minGap) {
      for (let j = i - run; j < i; j += 1) keep.add(j);
    }
    run = 0;
  }

  const out: DiffSegment[] = [];
  let gap = 0;
  const flush = () => {
    if (gap > 0) out.push({ kind: "gap", count: gap });
    gap = 0;
  };
  lines.forEach((line, index) => {
    if (keep.has(index)) {
      flush();
      out.push({ kind: "line", index, line });
    } else {
      gap += 1;
    }
  });
  flush();
  return out;
}
